/*  
<summary>
  Functional Component: StaffList
  Developer: Sahil Bharti
  Created Date: 03-Jan-2025
  Description: Renders a staff management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Staff Management interface</returns>
*/

import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import Grid, { getColumns } from "../../../shared-components/Grid/Grid";

import { CellContext } from "@tanstack/react-table";
import { observer } from "mobx-react";
import demoStoreIcon from "../../../assets/Images/man.png";
import { ColumnType } from "../../../constants/enums/grid-column-type-enum";
import userSettingEnum from "../../../constants/enums/user-setting-enum";
import { useStore } from "../../../contexts/store-provider";
import { IUpdateStaffManagement } from "../../../models/forms/IAddStaff";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import { ISorting } from "../../../models/ISorting";
import LoaderButton from "../../../shared-components/button/loader-button";
import Pagination from "../../../shared-components/Grid/Pagination";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";

import { useHistory, useLocation } from "react-router-dom";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { staffStoreInitialFilterState } from "../../../core/initialState/initial-filter-state";
import {
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import StoreAddStaffManagement from "./components/add-edit-store-staff-information";
import "./store-staff-information.css";
const StoreStaffList: React.FC = (props: any) => {
  const { staffManagementStore, preferencesStore, masterManagementStore } =
    useStore();
  const [openPanel, setOpenPanel] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const {
    inProgress,
    error,
    GetStaffManagementListService,
    allStaffManagement,
    staffList,
    resetAddUpdateStaffManagementState,
    GetStaffManagementFreewordFilterService,
    addUpdateStaffManagementState,
    deleteStaffManagementState,
    DeleteStaffManagementService,
    resetDeleteStaffManagement,
    GetStaffManagementByIdService,
    setStaffStoreFilterDetail,
    resetStaffStoreFilterDetail,
    getStaffStoreFilter,
    resetStaffManagementDetail,
  } = staffManagementStore;

  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const [prefectureNameSearch, setPrefectureNameSearch] = useState("");
  const [staffNameSearch, setStaffNameSearch] = useState(
    staffStoreInitialFilterState.StaffName ?? ""
  );
  const [showInvalidStores, setShowInvalidStores] = useState(
    staffStoreInitialFilterState.IsInvalid ?? false
  );

  let staffId = (location.state as { staffId?: number })?.staffId ?? -1;
  const [staffIdNo, setStaffIdNo] = useState<number>(staffId);

  let storeIdSearch = 0;
  const userType = getUserTypeFromLocalStorage();
  if (userType === userTypeEnum[userTypeEnum.Store]) {
    storeIdSearch = getStoreIdFromLocalStorage() || 0;
  }

  const handlePrefectureNameSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPrefectureNameSearch(e.target.value);
    },
    []
  );

  const handleStaffNameSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setStaffNameSearch(e.target.value);
    },
    []
  );

  const handleInvalidStaffFilter = useCallback(() => {
    setSearchText("");
    setFreeWordName("");
    setShowInvalidStores((prev) => !prev);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  }, []);

  const clearFilterHandler = () => {
    resetStaffStoreFilterDetail();
    setFreeWordName("");
    setSearchText("");
    setShowInvalidStores(false);
    setPrefectureNameSearch("");
    setStaffNameSearch("");
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  }

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [selectedId, setSelectedId] = useState<number>(0);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [freeWordName, setFreeWordName] = useState("");
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [sorting, setSorting] = useState<ISorting>({
    column: "order_id",
    orderBy: "asc",
  });

  const [modalShow, setModalShow] = useState(false);
  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [editingStore, setEditingStore] = useState<
    IUpdateStaffManagement | undefined
  >(undefined);

  useEffect(() => {
    if (staffIdNo > -1) {
      openAddStaffManagementHandler({ Id: staffIdNo });
    }
    resetStaffManagementDetail();
  }, [staffIdNo]);

  const handleEditStore = useCallback(() => {
    setDetailsModalShow(false);
    setModalShow(true);
  }, []);

  const handleFreewordSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFreeWordName(e.target.value);
    },
    []
  );

  const handleSeeDetails = useCallback((store: IUpdateStaffManagement) => {
    setEditingStore(store);
    setDetailsModalShow(true);
  }, []);

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  /**
   * This function calls the "getAllGroup" staff funtion that get configuration list from API
   */
  const callGetStaffManagementListService = useCallback(() => {
    if (searchText) {
      GetStaffManagementFreewordFilterService(
        pageInfo.currentPage,
        pageLimitInfo.pageLimit,
        searchText
      );
    } else {
      GetStaffManagementListService(
        pageInfo.currentPage,
        pageLimitInfo.pageLimit,
        sorting.orderBy,
        sorting.column,
        {
          IsInvalid: getStaffStoreFilter.IsInvalid,
          StaffName: getStaffStoreFilter.StaffName,
          IsFilterActive: getStaffStoreFilter.IsFilterActive,
        }
      );
    }
  }, [
    searchText,
    GetStaffManagementFreewordFilterService,
    pageInfo.currentPage,
    pageLimitInfo.pageLimit,
    GetStaffManagementListService,
    sorting.orderBy,
    sorting.column,
    getStaffStoreFilter.IsInvalid,
    getStaffStoreFilter.StaffName,
    getStaffStoreFilter.IsFilterActive,
  ]);

  useEffect(() => {
    callGetStaffManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetStaffManagementListService]);
  
  useEffect(() => {
    return(()=>{
      clearFilterHandler();
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStaffStoreFilterDetail({
      ...getStaffStoreFilter,
      IsFilterActive: true,
      IsInvalid: showInvalidStores,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInvalidStores]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetStaffManagementListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  useEffect(() => {
    if (freeWordName === "" && !showInvalidStores && staffNameSearch === "") {
      resetStaffStoreFilterDetail();
      setFreeWordName("");
      setSearchText("");
      setShowInvalidStores(false);
      setPrefectureNameSearch("");
      setStaffNameSearch("");
      setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
    }
  }, [
    freeWordName,
    resetStaffStoreFilterDetail,
    showInvalidStores,
    staffNameSearch,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: staffList?.PagingDetails?.TotalPages,
      isPagerChange: false,
      isRefreshPage: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStaffManagement]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetStaffManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStaffManagementState.success) {
      resetAddUpdateStaffManagementState();
      callGetStaffManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateStaffManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteStaffManagementState.success) {
      resetDeleteStaffManagement();
      if (allStaffManagement?.length === 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else {
          callGetStaffManagementListService();
        }
      } else {
        callGetStaffManagementListService();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteStaffManagementState.success]);
  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddStaffManagementHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler({ storeId: storeIdSearch });
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetStaffManagementListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function deletes the user by providing Id to the Api
   */
  const deleteHandler = () => {
    // if (getStaffStoreFilter.StoreId)
    //   DeleteStaffManagementService(selectedId, getStaffFilter.StoreId);
  };

  /**
   * This function shows the delete user confirmation popup component and sets the seleted user Id
   */
  const deleteClickHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.deletePopupToggleHandler();
  };

  const handleSearch = () => {
    resetStaffStoreFilterDetail();
    setStaffNameSearch(staffStoreInitialFilterState.StaffName ?? "");
    setShowInvalidStores(staffStoreInitialFilterState.IsInvalid ?? false);
    setSearchText(freeWordName);
    if (freeWordName.length > 0) {
      setStaffStoreFilterDetail({
        ...getStaffStoreFilter,
        IsFilterActive: true,
        Freeword: freeWordName,
        IsInvalid: false,
      });
    }
    setShowInvalidStores(false);
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };

  const handleStaffSearch = () => {
    resetStaffStoreFilterDetail();
    setSearchText("");
    setFreeWordName("");
    if (staffNameSearch.length > 0) {
      setStaffStoreFilterDetail({
        ...getStaffStoreFilter,
        IsFilterActive: true,
        StaffName: staffNameSearch,
        IsInvalid: showInvalidStores,
      });
    }
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  };

  /**
   * This function provides manual refresh functionality
   */
  const refreshClickHandler = () => {
    callGetStaffManagementListService();
  };

  const columns = getColumns<any, number>(
    [
      {
        columnType: ColumnType.Hidden,
        accessorKey: "Id",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "OrderId",
        displayName: "order_id",
        sortingName: "order_id",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <img
                className="grid-img"
                src={row.Image ?? demoStoreIcon}
                alt="store img"
              />
            </>
          );
        },
        accessorKey: "Image",
        displayName: "",
        headerClassName: "img-column",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "StaffName",
        displayName: "staff_name",
        sortingName: "user_name",
      },
      {
        columnType: ColumnType.TextHeader,
        accessorKey: "TelephoneNumber",
        displayName: "telephone",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "PrefectureName",
        displayName: "prefecture_name",
        sortingName: "prefecture_name",
      },
      // {
      //   columnType: ColumnType.Sorting,
      //   accessorKey: "StoreName",
      //   displayName: "store_name",
      //   sortingName: "store_name",
      // },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                openAddStaffManagementHandler(row);
              }}
            >
              <i className="bi bi-eye me-2"></i>
              <FormattedMessage id="see_details" />
            </Button>
          );
        },
        accessorKey: "isEdit",
        displayName: "",
        headerClassName: "last-column",
      },
    ],
    sorting,
    handleSortingChange
  );

  return (
    <React.Fragment>

      {(inProgress) && (
        <FormLoader
          loading={inProgress}
        />
      )}

      {props.showAddPopup && (
        <StoreAddStaffManagement
          id={selectedId}
          storeId={storeIdSearch}
          modalClosed={props.addToggleHandler}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={deleteStaffManagementState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="staff_information" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="store_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="staff_information" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>

      {storeIdSearch > 0 && (
        <div className="container-fluid mt-4 p-0">
          <div className="row align-items-center g-2 justify-content-between mb-3">
            <div className="col-md-7 col-lg-8 search_box">
              <div className="row g-2">
                <div className="col-sm-6 col-12 col-lg-4">
                  <div className="input-group">
                    <div className="input-group-text">
                      <span role="button" onClick={handleSearch}>
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={formatMessage("search_by_free_word")}
                      value={freeWordName}
                      onChange={handleFreewordSearch}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                      }}
                    />
                  </div>
                </div>

                <div className="col-sm-6 col-12 col-lg-4">
                  <div className="input-group">
                    <div className="input-group-text">
                      <span role="button" onClick={handleStaffSearch}>
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={formatMessage("search_by_staff_name")}
                      value={staffNameSearch}
                      onChange={handleStaffNameSearch}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleStaffSearch();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-5 col-lg-4">
              <div className="d-flex justify-content-end align-items-center">
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={showInvalidStores}
                  onChange={() => handleInvalidStaffFilter()}
                />
                <label className="form-check-label ms-1">
                  {formatMessage("show_invalid_staffs")}
                </label>
                {getStaffStoreFilter.IsFilterActive && (
                  <button
                    className={`ms-3 btn ${
                      getStaffStoreFilter.IsFilterActive
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={clearFilterHandler}
                    title={formatMessage(
                      getStaffStoreFilter.IsFilterActive
                        ? "clear_filter"
                        : "apply_filter"
                    )}
                  >
                    <svg
                      id="fi_3126668"
                      enableBackground="new 0 0 512 512"
                      height="18"
                      viewBox="0 0 512 512"
                      width="18"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#ffffff"
                    >
                      <g>
                        <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                        <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                      </g>
                    </svg>
                  </button>
                )}
                <button
                  className="btn btn-primary ms-3"
                  onClick={() => {
                    openAddStaffManagementHandler({ Id: -1 });
                  }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  {formatMessage("add_staff")}
                </button>
              </div>
            </div>
          </div>

          {allStaffManagement.length > 0 ? (
            <div className={`${openPanel ? "panelOpen" : ""}`}>
              <div>
                <div className="card changingPanel staffInformationTable">
                  <div className="tableDesign row-eq-height tableDevices">
                    <div className="table-responsive" data-testid="table">
                      {allStaffManagement && (
                        <Grid
                          data={allStaffManagement}
                          columns={columns}
                          isInProgress={false}
                          hiddenColumns={["Id"]}
                        />
                      )}
                      {inProgress && <FormLoader loading={inProgress} />}
                    </div>
                  </div>
                </div>

                {allStaffManagement.length > 0 ? (
                  <div className="row no-gutters p-2 tableFooter">
                    <div className="col-6 d-flex justify-content-end">
                      <Pagination
                        isRefreshPage={pageInfo.isRefreshPage}
                        totalPages={pageInfo.totalPages}
                        pageLimit={pageLimitInfo.pageLimit}
                        onPageChanged={onPageChanged}
                        disablePagination={disablePagination}
                        setIsRefreshPage={setIsRefreshPage}
                      />
                    </div>
                    <div className="col-6 pt-1">
                      {/* <FormattedMessage id="show" /> */}
                      <select
                        data-testid="pageSelect"
                        className="pageLimit"
                        onChange={onPageLimitChanged}
                        value={pageLimitInfo.pageLimit}
                      >
                        {pageLimitOptions.map((op) => (
                          <option key={op} value={op}>
                            {op} {formatMessage("rows")}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="message text-center">
              <img src={noMessageIcon} alt="" className="no_message" />

              {staffNameSearch.trim().length > 0 ||
              prefectureNameSearch.trim().length > 0 ||
              storeIdSearch !== 0 ||
              showInvalidStores ? (
                <>
                  <p className="noData text-muted text-center h4 mb-0">
                    <FormattedMessage id="no_data_available" />
                  </p>
                </>
              ) : (
                <>
                  <p className="noData text-muted text-center h4 mb-0">
                    <FormattedMessage id="no_staff" />
                  </p>
                  <p className="mb-3">{formatMessage("no_staff_message")}</p>
                  <LoaderButton
                    text="add_staff"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      openAddStaffManagementHandler({ Id: -1 })
                    }
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(StoreStaffList)))
);
